"use client";
import Stack from "@mui/material/Stack";
import { useState } from "react";
import { type AnyZodObject } from "zod";
import Form from "@/components/common/Form";
import FormContent from "@/components/site-builder/modules/CustomFormModule/components/FormContent";
import FormControlButtons from "@/components/site-builder/modules/CustomFormModule/components/FormControlButtons";
import FormError from "@/components/site-builder/modules/CustomFormModule/components/FormError";
import { FormPrivacyConsent } from "@/components/site-builder/modules/CustomFormModule/components/FormPrivacyConsent";
import FormTitle from "@/components/site-builder/modules/CustomFormModule/components/FormTitle";
import ThankYouMessage from "@/components/site-builder/modules/CustomFormModule/components/ThankYouMessage";
import { useCustomFormConsentConfig } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormConsentConfig";
import { useCustomFormSetConsent } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormSetConsent";
import { useCustomFormSetStateOfResidenceValidation } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormSetStateOfResidenceValidation";
import { useCustomFormWithValidation } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormWithValidation";

export default function CustomForm() {
  const [consentValidationSchema, setConsentValidationSchema] =
    useState<AnyZodObject | null>(null);
  const [
    stateOfResidenceValidationSchema,
    setStateOfResidenceValidationSchema,
  ] = useState<AnyZodObject | null>(null);

  const {
    form,
    submitHandler,
    isShownThankYouMessage,
    isError,
    error,
    resetFormSubmission,
  } = useCustomFormWithValidation({
    externalSchemas: [
      consentValidationSchema,
      stateOfResidenceValidationSchema,
    ],
  });
  const { control } = form;

  const {
    data: consentConfig,
    isLoading: isLoadingConsent,
    isError: isErrorConsent,
    error: errorConsent,
  } = useCustomFormConsentConfig({
    control,
  });
  const isShownConsentForm =
    !!consentConfig && !isLoadingConsent && !isErrorConsent;

  useCustomFormSetConsent({
    form,
    consentConfig,
    isShownConsentForm,
    setConsentValidationSchema,
  });
  useCustomFormSetStateOfResidenceValidation({
    form,
    setStateOfResidenceValidationSchema,
  });

  if (isShownThankYouMessage) {
    return <ThankYouMessage />;
  }

  return (
    <Form form={form} onSubmit={submitHandler}>
      <Stack spacing={3}>
        <FormTitle />
        <FormContent />
        {isShownConsentForm && (
          <FormPrivacyConsent consentConfig={consentConfig} />
        )}
        {isError && error && <FormError message={error.message} />}
        {isErrorConsent && errorConsent && (
          <FormError message={errorConsent.message} />
        )}
        <FormControlButtons
          submitDisabled={isLoadingConsent}
          resetFormSubmission={resetFormSubmission}
        />
      </Stack>
    </Form>
  );
}
